import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../assets/stylesheets/ViewReport.css';
import { clearLatestReportIfMatches } from './LatestReportBadge';
import { EVENT_NAMES, PARAM_KEYS, trackEvent } from '../analytics';

const BASE_API_URL = 'https://api.home-helper.com';
const HEADING_SCROLL_MARGIN = '88px';

/**
 * ViewReport.jsx
 *
 * Displays a previously generated  report for a customer.
 * This page is READ-ONLY and does not trigger  generation.
 */
export default function ViewReport() {
  const { customerId } = useParams();
  const reportUrl = customerId ? `/report/${customerId}` : '';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reportMarkdown = useMemo(
    () => normalizeReportMarkdown(report?.textReport || ''),
    [report?.textReport]
  );
  const tocItems = useMemo(() => buildReportToc(reportMarkdown), [reportMarkdown]);
  const tocIdsByKey = useMemo(() => {
    const map = new Map();

    tocItems.forEach((item) => {
      const existing = map.get(item.key) || [];
      existing.push(item.id);
      map.set(item.key, existing);
    });

    return map;
  }, [tocItems]);

  const renderedAnchorCounts = {};

  const getAnchorId = (text) => {
    const key = normalizeTocLabel(text);
    if (!key) {
      return '';
    }

    const ids = tocIdsByKey.get(key);
    if (!ids || ids.length === 0) {
      return '';
    }

    const nextIndex = renderedAnchorCounts[key] || 0;
    renderedAnchorCounts[key] = nextIndex + 1;

    return ids[nextIndex] || ids[ids.length - 1] || '';
  };

  useEffect(() => {
    if (!customerId) {
      setError('Missing customer identifier');
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      try {
        const res = await fetch(`${BASE_API_URL}/api/ai/reports/${customerId}`);

        if (!res.ok) {
          const contentType = res.headers.get('content-type') || '';
          const errorPayload = contentType.includes('application/json')
            ? await res.json()
            : null;
          throw new Error(errorPayload?.error || 'Failed to load report');
        }

        const data = await res.json();
        setReport(data);
        trackEvent(EVENT_NAMES.REPORT_VIEWED, {
          [PARAM_KEYS.CATEGORY_KEY]: data.categoryKey,
          [PARAM_KEYS.HAS_PHOTOS]: Array.isArray(data.imageUrls) && data.imageUrls.length > 0,
          [PARAM_KEYS.PHOTO_COUNT]: Array.isArray(data.imageUrls) ? data.imageUrls.length : undefined,
          [PARAM_KEYS.REPORT_TYPE]: 'project_summary',
          [PARAM_KEYS.PLACEMENT]: 'saved_report_page',
        });
      } catch (err) {
        console.error('❌ Failed to load report:', err);
        if (reportUrl) {
          clearLatestReportIfMatches(reportUrl);
        }
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [customerId, reportUrl]);

  return (
    <>
      <Header />

      <main className="view-report-page">
        {loading && (
          <div className="report-state">
            <p>Loading your report…</p>
          </div>
        )}

        {error && (
          <div className="report-state error">
            <h2>Unable to load report</h2>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && report && (
          <div className="view-report-container">
            <header className="report-header">
              <h1>Your Project Report</h1>
              <div className="report-meta">
                <span>
                  Category: <strong>{report.categoryKey}</strong>
                </span>
                <span>
                  Generated on{' '}
                  {new Date(report.createdAt).toLocaleString()}
                </span>
              </div>
            </header>

            {tocItems.length > 0 && (
              <aside className="report-toc" aria-label="Project contents">
                <div className="report-toc-title">Contents</div>
                <ul className="report-toc-list">
                  {tocItems.map((item) => (
                    <li key={item.id} className="report-toc-item">
                      <a
                        href={`#${item.id}`}
                        className="report-toc-link"
                        onClick={(event) => {
                          event.preventDefault();
                          const element = document.getElementById(item.id);

                          if (!element) {
                            return;
                          }

                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });

                          try {
                            window.history.replaceState(null, '', `#${item.id}`);
                          } catch (historyError) {
                            // Ignore history update failures.
                          }
                        }}
                      >
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            {/* Uploaded Images */}
            {Array.isArray(report.imageUrls) &&
              report.imageUrls.length > 0 && (
                <section className="report-section">
                  <h2>Uploaded Images</h2>
                  <div className="report-image-grid">
                    {report.imageUrls.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`Uploaded ${idx + 1}`}
                        loading="lazy"
                      />
                    ))}
                  </div>
                </section>
              )}

            {/* AI Generated Report */}
            <section className="report-section">
              <div className="report-markdown">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1({ node, children, ...props }) {
                      const id = getAnchorId(extractNodeText(node));

                      return (
                        <h1
                          id={id || undefined}
                          style={{ scrollMarginTop: HEADING_SCROLL_MARGIN }}
                          {...props}
                        >
                          {children}
                        </h1>
                      );
                    },
                    h2({ node, children, ...props }) {
                      const id = getAnchorId(extractNodeText(node));

                      return (
                        <h2
                          id={id || undefined}
                          style={{ scrollMarginTop: HEADING_SCROLL_MARGIN }}
                          {...props}
                        >
                          {children}
                        </h2>
                      );
                    },
                    h3({ node, children, ...props }) {
                      const id = getAnchorId(extractNodeText(node));

                      return (
                        <h3
                          id={id || undefined}
                          style={{ scrollMarginTop: HEADING_SCROLL_MARGIN }}
                          {...props}
                        >
                          {children}
                        </h3>
                      );
                    },
                    p({ node, children, ...props }) {
                      const id = getAnchorId(resolveParagraphAnchorText(node));

                      return (
                        <p
                          id={id || undefined}
                          style={id ? { scrollMarginTop: HEADING_SCROLL_MARGIN } : undefined}
                          {...props}
                        >
                          {children}
                        </p>
                      );
                    },
                  }}
                >
                  {reportMarkdown}
                </ReactMarkdown>
              </div>
            </section>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}

function normalizeReportMarkdown(markdown = '') {
  return String(markdown).replace(/^\s*---\s*$/gim, '').trim();
}

function buildReportToc(markdown = '') {
  const counts = new Map();
  const items = [];
  const lines = String(markdown).split(/\r?\n/);

  lines.forEach((line) => {
    const text = detectHeadingText(line);

    if (!text) {
      return;
    }

    const key = normalizeTocLabel(text);
    const baseId = slugify(key);

    if (!key || !baseId) {
      return;
    }

    const nextCount = (counts.get(baseId) || 0) + 1;
    counts.set(baseId, nextCount);

    items.push({
      text,
      key,
      id: nextCount === 1 ? baseId : `${baseId}-${nextCount}`,
    });
  });

  return items;
}

function detectHeadingText(line = '') {
  const trimmed = String(line).trim();

  if (!trimmed) {
    return '';
  }

  const markdownHeadingMatch = trimmed.match(/^#{1,6}\s+(.+?)\s*#*$/);
  if (markdownHeadingMatch) {
    return normalizeTocLabel(markdownHeadingMatch[1]);
  }

  if (/^Project Report:/i.test(trimmed)) {
    return normalizeTocLabel(trimmed);
  }

  if (/^\d+\.\s+/.test(trimmed)) {
    return normalizeTocLabel(trimmed);
  }

  const bulletStrongMatch = trimmed.match(/^[-*+]\s+(?:\*\*|__)(.+?)(?::)?(?:\*\*|__)\b/);
  if (bulletStrongMatch) {
    return normalizeTocLabel(bulletStrongMatch[1]);
  }

  const strongLineMatch = trimmed.match(/^(?:\*\*|__)(.+?)(?:\*\*|__)$/);
  if (strongLineMatch) {
    return normalizeTocLabel(strongLineMatch[1]);
  }

  return '';
}

function resolveParagraphAnchorText(node) {
  const fullText = normalizeTocLabel(extractNodeText(node));
  const detectedHeading = detectHeadingText(fullText);

  if (detectedHeading) {
    return detectedHeading;
  }

  if (!node || !Array.isArray(node.children) || node.children.length === 0) {
    return '';
  }

  const firstChild = node.children[0];
  if (!firstChild || (firstChild.type !== 'strong' && firstChild.type !== 'emphasis')) {
    return '';
  }

  return normalizeTocLabel(extractNodeText(firstChild));
}

function extractNodeText(node) {
  if (!node) {
    return '';
  }

  if (typeof node.value === 'string') {
    return node.value;
  }

  if (!Array.isArray(node.children)) {
    return '';
  }

  return node.children.map((child) => extractNodeText(child)).join('');
}

function normalizeTocLabel(text = '') {
  return String(text).replace(/\s+/g, ' ').trim().replace(/:\s*$/, '');
}

function slugify(text = '') {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

